import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErrorBox, Loading, Modal, errorText, useAsync, useToast } from '../../shared/ui';
import { api, type PortalSession, type SupplierRow } from '../api';
import { usePortal } from '../context';

/**
 * «Ver como proveedor»: mirar el portal exactamente como lo vería un
 * proveedor (sus pedidos, su evaluación, sus planos), sin poder cambiar
 * nada. Solo para los usuarios autorizados (la API lo decide).
 */

export function ViewAsButton(): JSX.Element | null {
  const { me } = usePortal();
  const [open, setOpen] = useState(false);
  if (!me.canViewAs) return null;
  return (
    <>
      <button className="btn sm" onClick={() => setOpen(true)} title="Ver el portal como un proveedor y actuar en su nombre">
        Ver como proveedor
      </button>
      {open ? <ViewAsModal onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function ViewAsModal({ onClose }: { onClose: () => void }): JSX.Element {
  const { reloadMe } = usePortal();
  const navigate = useNavigate();
  const toast = useToast();
  const suppliers = useAsync(() => api.get<{ suppliers: SupplierRow[] }>('/portal/suppliers'), []);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (suppliers.data?.suppliers ?? [])
      .filter((s) => s.enabled)
      .filter((s) => t === '' || s.name.toLowerCase().includes(t) || s.cardCode.toLowerCase().includes(t));
  }, [suppliers.data, q]);

  async function start(cardCode: string): Promise<void> {
    setBusy(cardCode);
    try {
      const res = await api.post<{ token: string; session: PortalSession }>('/portal/view-as', { cardCode });
      api.setToken(res.token);
      await reloadMe();
      navigate('/', { replace: true });
      onClose();
    } catch (err) {
      toast(errorText(err), 'bad');
      setBusy(null);
    }
  }

  return (
    <Modal title="Ver el portal como un proveedor" onClose={onClose}>
      <div className="stack">
        <p className="muted">
          Verás el portal exactamente como el proveedor: sus pedidos, su evaluación y sus planos, y podrás actuar en su nombre (aceptar, fechas,
          albaranes). Todo lo que hagas queda registrado <strong>a tu nombre</strong>, en nombre del proveedor.
        </p>
        <input className="input" placeholder="Buscar proveedor por nombre o código" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
        {suppliers.error ? (
          <ErrorBox error={suppliers.error} onRetry={() => void suppliers.reload()} />
        ) : !suppliers.data ? (
          <Loading />
        ) : list.length === 0 ? (
          <p className="subtle">No hay proveedores con acceso que coincidan.</p>
        ) : (
          <div className="pt-viewas-list">
            {list.map((s) => (
              <button key={s.cardCode} className="btn block pt-viewas-item" disabled={busy !== null} onClick={() => void start(s.cardCode)}>
                <span className="pt-viewas-name">{s.name}</span>
                <span className="subtle mono">{busy === s.cardCode ? 'Abriendo…' : s.cardCode}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

/** Franja fija mientras se mira como proveedor, con la vuelta a la vista propia. */
export function ViewAsBanner(): JSX.Element | null {
  const { me, reloadMe } = usePortal();
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  if (!me.viewAs) return null;

  async function exit(): Promise<void> {
    setBusy(true);
    try {
      const res = await api.post<{ token: string; session: PortalSession }>('/portal/view-as/exit');
      api.setToken(res.token);
      await reloadMe();
      navigate('/', { replace: true });
    } catch (err) {
      toast(errorText(err), 'bad');
      setBusy(false);
    }
  }

  return (
    <div className="pt-viewas-banner" role="status">
      <span>
        👁 Estás viendo el portal como <strong>{me.viewAs.supplierName}</strong> · lo que hagas queda a tu nombre
      </span>
      <button className="btn sm" onClick={() => void exit()} disabled={busy}>
        {busy ? 'Volviendo…' : 'Volver a mi vista'}
      </button>
    </div>
  );
}
